import type { BillingCycle, TierCode } from '../api/billingApi';

// 카드명·설명·기능리스트·체험기간·연간무료개월은 저장 테이블(SYS_POLICY_CONFIG·TM_TIER_FEATURE)이
// 아직 확정되지 않아 FE 정적 리소스로 관리한다(사용자 결정, 2026-08-27). DB화가 결정되면
// API 응답값으로 대체한다.
export interface TierCopy {
  displayName: string;
  description: string;
  ctaLabel: string;
  ctaVariant: 'primary' | 'secondary';
  featureList: string[];
  /** 910 §4.1 티어별 포함 에이전트 개수. 구체적으로 어떤 에이전트인지는 아직 미정(TM_TIER_AGENT
   *  시드 미확정, 추후작업_TODO.md #7) — 개수 안내까지만 노출한다(사용자 결정, 2026-08-27). */
  agentCountLabel: string;
}

export const TIER_COPY: Record<TierCode, TierCopy> = {
  FREE: {
    displayName: 'Free',
    description: '일단 가볍게, 부담 없이 시작',
    ctaLabel: '무료로 시작하기',
    ctaVariant: 'secondary',
    featureList: [
      '내 현장사진 정리하기',
      '고객 사진 정리하기',
      '시공반장 사진 정리하기',
      'PC 사진 정리하기',
      '견적서로 스케줄 자동생성',
      '스케줄 직접 만들기',
      '스케줄 자유롭게 조정하기',
      '동시 진행 현장 1개까지',
      '카드 등록 필요 없음',
    ],
    agentCountLabel: '1개 (인디포토 포함)',
  },
  STARTER: {
    displayName: 'Starter',
    description: '디지털 전환, 처음 해보는 사장님용',
    // 체험·결제 이력 있으면 즉시 결제, 없으면 1개월 무료체험(사용자 결정, 2026-09-07) — 이 라벨은
    // "체험 이미 써서 즉시 결제"하는 경우의 기본값. 체험 대상이면 resolveCtaLabel이 대신 표시한다.
    ctaLabel: '시작하기',
    ctaVariant: 'secondary',
    featureList: [
      'Free의 모든 기능 포함',
      '정리한 사진 공유하기',
      '스케줄 공유하기',
      '동시 진행 현장 3개까지',
    ],
    agentCountLabel: '3개 (인디포토 포함)',
  },
  PRO: {
    displayName: 'Pro',
    description: '현장이 손에 잡히기 시작한 사장님용',
    ctaLabel: '시작하기',
    ctaVariant: 'primary',
    featureList: [
      'Starter의 모든 기능 포함',
      '시공사례 만들기',
      '시공사례 블로그 공유하기',
      '전체 현장 일정 확인하기',
      '동시 진행 현장 8개까지',
      'AI 견적 등 고도화 기능 포함',
    ],
    agentCountLabel: '5개 (인디포토 포함)',
  },
  ALLIN: {
    displayName: 'All-in',
    description: '현장 여러 개 돌리는 헤비유저용',
    ctaLabel: '시작하기',
    ctaVariant: 'secondary',
    featureList: [
      'Pro의 모든 기능 포함',
      '향후 추가되는 모든 작업 포함',
      '동시 진행 현장 무제한',
    ],
    agentCountLabel: '전체',
  },
};

export const TIER_ORDER: TierCode[] = ['FREE', 'STARTER', 'PRO', 'ALLIN'];

/** 무료체험 기간(개월). 정책 테이블 미확정으로 FE 하드코딩(사용자 결정, 2026-08-27) */
export const TRIAL_PERIOD_MONTHS = 1;

/** 연간결제 무료개월 수. 정책 테이블 미확정으로 FE 하드코딩(사용자 결정, 2026-08-27) */
export const FREE_MONTHS_ANNUAL = 2;

/** 월간/연간 결제 주기 토글(Segmented) 선택지 — 요금제 비교(PricingPlansPage)·요금제 변경
 *  (TierChangePage) 화면이 공유한다(2026-09-17, 사용자 요청 — 전엔 TierChangePage 쪽만 연간
 *  옵션에 "N개월 무료" 문구가 빠져 있었음). */
export const BILLING_CYCLE_OPTIONS: { label: string; value: BillingCycle }[] = [
  { label: '월간 결제', value: 'monthly' },
  { label: `연간 결제 · ${FREE_MONTHS_ANNUAL}개월 무료`, value: 'annual' },
];

/** 추천 코드로 가입 시 무료체험에 더해지는 보너스 개월 수 — BE
 *  ReferralService.REFEREE_BONUS_TRIAL_MONTHS와 동일 값(현재 1개월, 기본체험과 합쳐 총 2개월).
 *  실제 유효성(코드 존재·자기추천 아님·중복 아님)은 BE만 검증하므로, 여기서는 `?ref=` 파라미터가
 *  있으면 그대로 보너스가 붙는다고 낙관적으로 가정해 문구를 미리 보여준다(사용자 결정, 2026-09-08). */
export const REFERRAL_BONUS_TRIAL_MONTHS = 1;

/** 화면에 보여줄 체험 개월 수 — 추천 코드가 있으면(startTrial에 실려갈 ?ref= 존재) 보너스를 더한다. */
export function getTrialMonths(hasReferralCode: boolean): number {
  return TRIAL_PERIOD_MONTHS + (hasReferralCode ? REFERRAL_BONUS_TRIAL_MONTHS : 0);
}
